// ── AssistantMemoryPage — memoria entre sesiones de Evo Brain ───────────────
// Roadmap Evo Brain, capacidad 1 (2026-08-06): ver qué aprendió (preferencias,
// decisiones, formatos) y borrar lo que no aplica. Mismo List Pattern que
// /historial (AssistantHistoryPage.tsx) — sin entrada en sidebar, se llega
// desde el botón nuevo en el header del dock.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { ListItem } from '../components/ui/ListItem.js';
import { Button } from '../components/ui/Button.js';
import { Pill } from '../components/PillGroup.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { ListState } from '../components/ui/ListState.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { TimeBandedList } from '../components/common/TimeBandedList.js';
import { parseServerDate } from '../lib/date.js';
import { entityTypeNavKey } from '../lib/assistantModules.js';
import { useAssistantMemory, useForgetMemory, type MemoryEntry } from '../hooks/useAssistantMemory.js';

function fmt(iso: string): string {
  return parseServerDate(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function AssistantMemoryPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useAssistantMemory();
  const forget = useForgetMemory();
  const [moduleFilter, setModuleFilter] = useState('');

  const { setOverride } = useTopBarCtx();
  useEffect(() => {
    setOverride({ title: t('assistantMemory.pageTitle'), heroIcon: 'bookmark', heroIconColor: 'var(--evo-steel)' });
    return () => setOverride({});
  }, [setOverride, t]);

  const allEntries = data?.entries ?? [];

  // Opciones del filtro: solo módulos con al menos un hecho recordado.
  // 'general' agrupa lo aprendido sin conversación fuente (voz/externo).
  const moduleOptions = useMemo(() => {
    const types = new Set(allEntries.map(e => e.entityType ?? 'general'));
    return Array.from(types)
      .map(entityType => ({
        value: entityType,
        label: entityType === 'general'
          ? t('assistantMemory.moduleGeneral')
          : (entityTypeNavKey(entityType) ? t(entityTypeNavKey(entityType)) : entityType),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allEntries, t]);

  const entries = moduleFilter
    ? allEntries.filter(e => (e.entityType ?? 'general') === moduleFilter)
    : allEntries;

  function handleForget(entry: MemoryEntry) {
    if (confirm(t('assistantMemory.confirmForget'))) forget.mutate(entry.id);
  }

  return (
    <div className="flex flex-col h-full animate-fade-blur-in">
      <FilterRow>
        <FilterRow.Spacer />
        <FilterRow.Dropdown<string>
          value={moduleFilter}
          placeholder={t('assistantMemory.allModules')}
          options={[
            { value: '', label: t('assistantMemory.allModules') },
            ...moduleOptions,
          ]}
          onChange={val => setModuleFilter(val ?? '')}
        />
      </FilterRow>

      <div className="flex-1 overflow-auto">
        {isLoading && <LoadingState size="sm" />}

        {!isLoading && entries.length === 0 && (
          <ListState
            isLoading={false} isError={isError} error={error}
            isEmpty emptyTitle={t('assistantMemory.empty')}
            onRetry={() => { void refetch(); }}
          >{null}</ListState>
        )}

        {!isLoading && entries.length > 0 && (
          <TimeBandedList<MemoryEntry, number>
            items={entries}
            getId={e => e.id}
            getTimestamp={e => e.createdAt}
            checkedIds={new Set()}
            renderItem={entry => (
              <ListItem
                key={entry.id}
                itemId={entry.id}
                title={entry.content}
                meta={fmt(entry.createdAt)}
                badges={[
                  <Pill key="category" size="xs" tone="muted">
                    {t(`assistantMemory.category.${entry.category}`, { defaultValue: entry.category })}
                  </Pill>,
                ]}
                actions={
                  <Button
                    size="sm"
                    variant="outline"
                    tone="danger"
                    loading={forget.isPending && forget.variables === entry.id}
                    onClick={() => handleForget(entry)}
                  >
                    {t('assistantMemory.forget')}
                  </Button>
                }
              />
            )}
          />
        )}
      </div>
    </div>
  );
}
